<template>
  <div class="service-container">
    <div class="item logo-container">
      <a :href="sanitizeUrl" target="_blank" :title="`${title} Status Page`" data-cy="serviceLogo">
        <img class="logo" :src="`/img/${serviceKey}-logo.png`" alt="logo" />
      </a>
    </div>
    <div v-if="loading" class="icon" data-cy="loadingIcon">
      <v-progress-circular
        indeterminate
        color="primary"
        size="32"
        class="progress"
      ></v-progress-circular>
    </div>
    <div v-else class="item icon-container" data-cy="statusIcons">
      <v-icon class="icon" :color="iconColor" :icon="icon" size="36" />
    </div>
    <div class="item text-container">
      <span class="text" data-cy="text">
        {{ statusText }}
        <br />
        {{ checkedText }}
      </span>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import DOMPurify from 'dompurify'
import { NAMESPACES } from '@/store/types'
import { ADMIN_PANEL_ACTIONS, ADMIN_PANEL_GETTERS } from '@/store/adminpanel'
import type { ServiceHealthStatus } from '@/types/admin'

const STRIP_ALL_HTML: { ALLOWED_TAGS: string[]; ALLOWED_ATTR: string[] } = {
  ALLOWED_TAGS: [],
  ALLOWED_ATTR: []
}

export default defineComponent({
  name: 'ServiceComponent',
  props: {
    serviceKey: { type: String, required: true },
    title: { type: String, required: true },
    serviceUrl: { type: String, required: true }
  },
  data() {
    return {
      loading: true,
      seconds: -1,
      statusKey: '' as string,
      icon: '' as string,
      iconColor: '' as string,
      counterId: null as ReturnType<typeof setInterval> | null,
      pollerId: null as ReturnType<typeof setInterval> | null
    }
  },
  computed: {
    statusText(): string {
      return this.$t(`thirdPartyTable.${this.statusKey}`, {
        service: this.title
      }) as string
    },
    checkedText(): string {
      return this.$t('thirdPartyTable.checked', {
        seconds: this.seconds
      }) as string
    },
    serviceStatus(): ServiceHealthStatus | null {
      return this.$store.getters[
        `${NAMESPACES.ADMIN_PANEL}/${ADMIN_PANEL_GETTERS.GET_SERVICE_STATUS}`
      ](this.serviceKey)
    },
    sanitizeUrl(): string {
      return DOMPurify.sanitize(this.serviceUrl, STRIP_ALL_HTML)
    }
  },
  methods: {
    async healthChecker(service: string): Promise<ServiceHealthStatus> {
      return await this.$api.healthChecker(service)
    },
    async updateFields(): Promise<void> {
      this.seconds = 0
      this.statusKey = 'loadingStatus'
      this.loading = true
      await this.$store.dispatch(
        `${NAMESPACES.ADMIN_PANEL}/${ADMIN_PANEL_ACTIONS.UPDATE_SERVICE_STATUS}`,
        {
          key: this.serviceKey,
          status: await this.healthChecker(this.serviceKey)
        }
      )
      this.updateUI()
    },
    updateUI(): void {
      const response = this.serviceStatus
      switch (response as string | null) {
        case 'none':
          this.icon = 'mdi-check-circle'
          this.iconColor = 'success'
          this.statusKey = 'noneStatus'
          break
        case 'minor':
          this.icon = 'mdi-alert'
          this.iconColor = 'warning'
          this.statusKey = 'minorStatus'
          break
        case 'maintenance':
          this.icon = 'mdi-alert'
          this.iconColor = 'warning'
          this.statusKey = 'maintenanceStatus'
          break
        case 'error':
          this.icon = 'mdi-alert'
          this.iconColor = 'warning'
          this.statusKey = 'errorStatus'
          break
        default:
          this.icon = 'mdi-alert-circle'
          this.iconColor = 'error'
          this.statusKey = 'downStatus'
          break
      }
      this.loading = false
    },
    updateCount(): void {
      this.seconds++
    }
  },
  watch: {
    serviceStatus(): void {
      this.updateUI()
    }
  },
  created(): void {
    this.updateFields()
    this.counterId = setInterval(this.updateCount, 1000)
    this.pollerId = setInterval(async () => {
      await this.updateFields()
      this.updateUI()
    }, 61000)
  },
  unmounted(): void {
    if (this.counterId) clearInterval(this.counterId)
    if (this.pollerId) clearInterval(this.pollerId)
  }
})
</script>

<style lang="scss" scoped>
.service-container {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  width: 220px;
  margin-bottom: 20px;
  margin-left: 20px;
  margin-top: 10px;
}
.logo-container {
  a {
    display: flex;
    align-items: center;
  }
  .logo {
    height: 25px;
  }
}
.text {
  font-size: small;
}
.icon {
  margin-left: 10px;
}
.progress {
  margin: 2px;
}
</style>
